import { useAuth0 } from "@auth0/auth0-react";
import Lottie from "lottie-react";
import React, { useEffect, useRef, useState } from "react";
import waitingAnimation from "../assets/lottie/waiting_lottie.json";
import { getClientId } from "../config/domain";
import { useRequest } from "../context/RequestContext";
import { useUploadContext } from "../context/UploadContext";
import { FILE_TYPES } from "../enums/FileTypes";
import { createDocument, subscribeToDocument } from "../services/firestore";
import { uploadFile } from "../services/storage";
import ResultField from "./ResultField";

const FileUpload = ({
  title,
  id,
  onDataUpdate,
  className,
  selectedTemplateId,
  showErrorFields = false,
  dictionary = {},
  onPreCreatedDocumentProcessed,
  preloadedDocumentData,
}) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadedFileUrls, setUploadedFileUrls] = useState([]);
  const [documentIds, setDocumentIds] = useState([]);
  const [error, setError] = useState(null);
  const [filePreviewUrls, setFilePreviewUrls] = useState([]);
  const [fileInfos, setFileInfos] = useState([]);
  const { user } = useAuth0();
  const { requestId, requestError } = useRequest();
  const [documentStatus, setDocumentStatus] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [allUploadsReady, setAllUploadsReady] = useState(false);
  const {
    allFileUploadsComplete,
    registerFileUpload,
    markFileUploadReady,
    optionalUploads,
  } = useUploadContext();
  const [fileSpinners, setFileSpinners] = useState([]);

  // Use useRef to track if markFileUploadReady has been called
  const hasMarkedReady = React.useRef(false);

  // Add a ref to track which documents have been reported
  const reportedDocumentIds = useRef({});

  // Track if files are currently being uploaded or processed
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);

  // Add state to track if this is preloaded data from pre_created_document
  const [isPreloadedData, setIsPreloadedData] = useState(false);

  // Track previous documentIds to only subscribe to new ones
  const previousDocumentIds = useRef([]);

  // Check if this upload is optional
  const isOptional = optionalUploads && optionalUploads.includes(id);

  // For multi-file, store the index to replace
  const replaceFileIndex = useRef(null);

  // Helper to replace a file at given index after selecting a new file
  const replaceFileAtIndex = async (index, selectedFile) => {
    if (!user || !requestId) return;

    // Update local arrays to show new file preview and spinner
    const newFiles = [...files];
    newFiles[index] = selectedFile;
    setFiles(newFiles);

    const newInfos = [...fileInfos];
    newInfos[index] = {
      name: selectedFile.name,
      type: selectedFile.type,
      size: selectedFile.size,
    };
    setFileInfos(newInfos);

    const newSpinners = [...fileSpinners];
    newSpinners[index] = true;
    setFileSpinners(newSpinners);

    // Set preview URL for immediate feedback
    let previewUrl = null;
    if (selectedFile.type.startsWith("image/")) {
      previewUrl = URL.createObjectURL(selectedFile);
    } else if (selectedFile.type === "application/pdf") {
      previewUrl = URL.createObjectURL(selectedFile);
    }

    setFilePreviewUrls((prev) => {
      const arr = [...prev];
      arr[index] = previewUrl;
      return arr;
    });

    // Prepare upload
    try {
      const timestamp = Date.now();
      const sourceId = `${timestamp}`;
      const extension = selectedFile.name.split(".").pop().toLowerCase();
      const base = getClientId();
      const filePath = `${id}/${base}/${requestId}_${sourceId}.${extension}`;

      const url = await uploadFile(selectedFile, filePath, () => {});

      const docId = await createDocument({
        type: FILE_TYPES[id],
        url,
        extension,
        sourceId,
        requestId,
        status: "CREATED",
        name: selectedFile.name || "",
        subtype: id,
        selectedTemplateId,
        user_email: user?.email || "",
        domain: window.location.hostname,
        petition_type: selectedTemplateId || "",
      });

      // Update URL and documentId arrays
      setUploadedFileUrls((prev) => {
        const arr = [...prev];
        arr[index] = url;
        return arr;
      });

      // Once uploaded, switch preview to remote URL
      setFilePreviewUrls((prev) => {
        const arr = [...prev];
        arr[index] = url;
        return arr;
      });

      setDocumentIds((prev) => {
        const arr = [...prev];
        arr[index] = docId;
        // Update previousDocumentIds ref to include the new document ID
        previousDocumentIds.current = [...arr];
        return arr;
      });
    } catch (err) {
      console.error("Error replacing file", err);
      setError("Dosya yenilenemedi");
      // Reset spinner
      setFileSpinners((prev) => {
        const arr = [...prev];
        arr[index] = false;
        return arr;
      });
    }
  };

  // Function to preload document data from requests collection
  const preloadDocumentData = (requestDocumentData, templateId) => {
    // Check if we have data for this document type
    const docTypeData = requestDocumentData[id];
    docTypeData.map((doc) => {
      doc.subtype = id;
      doc.type = FILE_TYPES[id];
    });
    if (
      !docTypeData ||
      !Array.isArray(docTypeData) ||
      docTypeData.length === 0
    ) {
      console.log(`[${id}] No preloaded data found for this document type`);
      return;
    }

    // Create mock file objects for each document
    const mockFiles = docTypeData.map((doc, index) => {
      // Determine a mock file type based on the url value
      let fileType;
      if (doc.url === "no-file") {
        fileType = "no-file";
      } else if (doc.url && doc.url.endsWith(".pdf")) {
        fileType = "application/pdf";
      } else {
        fileType = "application/msword";
      }

      return {
        name: `${id}_document_${index + 1}.pdf`, // Mock filename
        type: fileType,
        size: 1024 * 100, // Mock 100KB size
      };
    });

    // Extract URLs for preview. For 'no-file' keep the string so that UI can recognise placeholder case.
    const previewUrls = docTypeData.map((doc) =>
      doc.url === "no-file" ? "no-file" : doc.url || ""
    );
    // Create file info objects
    const fileInfos = mockFiles.map((file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
    }));

    // Create mock document IDs
    const mockDocumentIds = docTypeData.map(
      (_, index) => `preloaded_${id}_${index}`
    );

    // Set all states as if upload completed successfully
    setFiles(mockFiles);
    setFileInfos(fileInfos);
    setFilePreviewUrls(previewUrls);
    setUploadedFileUrls(previewUrls);
    setDocumentIds(mockDocumentIds);
    setDocumentStatus(new Array(docTypeData.length).fill("DONE"));
    setDocumentData(docTypeData);
    setFileSpinners(new Array(docTypeData.length).fill(false));
    setIsUploadingFiles(false);
    setAllUploadsReady(true);
    setIsPreloadedData(true);

    console.log(`${id} has preloaded data, marking as ready`);

    // Mark as ready
    if (!hasMarkedReady.current) {
      markFileUploadReady(id);
      hasMarkedReady.current = true;
    }

    // Report the preloaded data to parent
    if (onDataUpdate && typeof onDataUpdate === "function") {
      onDataUpdate(docTypeData);
    }
  };

  // Helper function to get field title from dictionary with fallback to fieldId
  const getFieldTitle = (fieldId) => {
    return dictionary[fieldId] || fieldId;
  };

  // Helper function to get specific field name for receipts based on receipt type
  const getReceiptSpecificFieldName = (genericFieldId) => {
    if (!genericFieldId) return genericFieldId;

    // Mapping of receipt types to their specific field names
    const receiptFieldMappings = {
      depreciation_receipt: {
        transactionDate: "depreciationPaymentDate",
        amount: "depreciationPaymentAmount",
      },
      third_party_receipt: {
        transactionDate: "thirdPartyPaymentDate",
        amount: "thirdPartyPaymentAmount",
      },
      recourse_receipt: {
        transactionDate: "recoursePaymentDate",
        amount: "recoursePaymentAmount",
      },
      application_receipt: {
        transactionDate: "arbitrationApplicationDate",
        amount: "applicationAmount", // fallback if needed
      },
      damage_receipt: {
        transactionDate: "damagePaymentDate",
        amount: "damagePaymentAmount",
      },
    };

    // If this is a receipt type and we have a mapping, use the specific field name
    if (receiptFieldMappings[id] && receiptFieldMappings[id][genericFieldId]) {
      return receiptFieldMappings[id][genericFieldId];
    }

    return genericFieldId;
  };

  // Enhanced helper function to get field title, considering receipt-specific mappings
  const getReceiptFieldTitle = (fieldId) => {
    const specificFieldName = getReceiptSpecificFieldName(fieldId);
    return (
      dictionary[specificFieldName] ||
      dictionary[fieldId] ||
      specificFieldName ||
      fieldId
    );
  };

  // Function to render individual receipt result fields
  const renderReceiptFields = (data, index) => {
    return (
      <div className="result-fields">
        <ResultField
          title={getReceiptFieldTitle("transactionDate")}
          value={data.transactionDate}
          fieldId="transactionDate"
          onValueChange={(fieldId, newValue) =>
            handleFieldUpdate(index, fieldId, newValue)
          }
        />
        {data.subtype !== "application_receipt" && (
          <ResultField
            title={getReceiptFieldTitle("amount")}
            value={data.amount}
            fieldId="amount"
            onValueChange={(fieldId, newValue) =>
              handleFieldUpdate(index, fieldId, newValue)
            }
          />
        )}
        {data.subtype === "third_party_receipt" && (
          <ResultField
            title={getReceiptFieldTitle("thirdPartyPlate")}
            value={data.thirdPartyPlate}
            fieldId="thirdPartyPlate"
            onValueChange={(fieldId, newValue) =>
              handleFieldUpdate(index, fieldId, newValue)
            }
          />
        )}
        {data.subtype === "recourse_receipt" && (
          <ResultField
            title={getReceiptFieldTitle("recoursePaymentCompany")}
            value={data.recoursePaymentCompany}
            fieldId="recoursePaymentCompany"
            onValueChange={(fieldId, newValue) =>
              handleFieldUpdate(index, fieldId, newValue)
            }
          />
        )}
        {showErrorFields && (
          <ResultField title="Error" value={data.error} fieldId="error" />
        )}
      </div>
    );
  };

  // Function to render receipt results
  const renderReceiptResults = () => {
    return (
      <div className="success-message">
        {documentData.map((data, index) => (
          <div key={index}>{renderReceiptFields(data, index)}</div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    // Only register once when component mounts
    registerFileUpload(id);

    // If this is an optional document, mark it as ready immediately
    if (isOptional && !hasMarkedReady.current) {
      console.log(`${id} is optional, marking as ready immediately`);
      markFileUploadReady(id);
      hasMarkedReady.current = true;
      setAllUploadsReady(true);
    }

    // Reset the ref if component unmounts
    return () => {
      hasMarkedReady.current = false;
    };
  }, [id, registerFileUpload, isOptional, markFileUploadReady]);

  // Effect to handle preloaded document data
  useEffect(() => {
    if (
      preloadedDocumentData &&
      preloadedDocumentData[id] &&
      !isPreloadedData &&
      files.length === 0 // prevent re-processing after normal upload
    ) {
      preloadDocumentData(preloadedDocumentData, selectedTemplateId);
    }
  }, [
    preloadedDocumentData,
    id,
    selectedTemplateId,
    isPreloadedData,
    files.length,
  ]);

  // Separate effect to handle allFileUploadsComplete changes
  useEffect(() => {
    // Only set allUploadsReady from context if we have files
    // This prevents the context from overriding our local state management
    if (files.length > 0) {
      setAllUploadsReady(allFileUploadsComplete);
    }
  }, [allFileUploadsComplete, files.length]);

  // Effect to handle document data changes
  useEffect(() => {
    if (
      documentData &&
      documentStatus &&
      Array.isArray(documentStatus) &&
      documentStatus.includes("DONE") &&
      onDataUpdate &&
      typeof onDataUpdate === "function"
    ) {
      // Create a key that represents both document statuses *and* a hash of the data
      const dataHash = JSON.stringify(documentData || {});
      const statusKey = `${documentStatus.join(",")}|${dataHash}`;

      // Only report if this is a new status combination we haven't seen
      if (!reportedDocumentIds.current[statusKey]) {
        reportedDocumentIds.current[statusKey] = true;
        onDataUpdate(documentData);
      }
    }
  }, [documentData, documentStatus, onDataUpdate, id]);

  // Update spinner state when document status changes
  useEffect(() => {
    if (documentStatus && Array.isArray(documentStatus)) {
      const allDone = documentStatus.every((status) => status === "DONE");

      // Only set isUploadingFiles to false when ALL documents are done
      if (allDone) {
        setIsUploadingFiles(false);
        setAllUploadsReady(true);
      }

      setFileSpinners((prev) => {
        const newSpinners = [...prev];
        documentStatus.forEach((status, index) => {
          // If status is DONE, set spinner to false for that specific file
          if (status === "DONE" && newSpinners[index] === true) {
            newSpinners[index] = false;
          }
        });
        return newSpinners;
      });
    }
  }, [documentStatus]);

  useEffect(() => {
    const unsubscribes = [];

    // Only subscribe to new document IDs
    const newDocumentIds = documentIds.filter(
      (docId, index) => docId && !previousDocumentIds.current.includes(docId)
    );

    newDocumentIds.forEach((docId) => {
      const index = documentIds.indexOf(docId);
      if (docId) {
        console.log(`Setting up subscription for doc: ${docId}`);

        const unsubscribe = subscribeToDocument(docId, (doc) => {
          if (doc) {
            console.log(`Received document update for ${docId}:`, doc);

            // Update document status and data in arrays
            setDocumentStatus((prevStatus) => {
              const newStatus = Array.isArray(prevStatus)
                ? [...prevStatus]
                : [];
              newStatus[index] = doc.status;
              console.log(`Updated status for ${id}:`, newStatus);
              return newStatus;
            });
            setDocumentData((prevData) => {
              const newData = Array.isArray(prevData) ? [...prevData] : [];
              newData[index] = doc;
              console.log(`Updated data for ${id}:`, newData);
              return newData;
            });

            // If document is done processing, update upload state
            if (doc.status === "DONE") {
              setIsUploadingFiles(false);
              setAllUploadsReady(true);

              // Special handling for pre_created_document
              if (id === "pre_created_document") {
                console.log(
                  "Pre-created document processed, calling parent callback with requestId:",
                  doc.requestId
                );

                // Call parent callback to handle the pre_created_document processing
                if (
                  onPreCreatedDocumentProcessed &&
                  typeof onPreCreatedDocumentProcessed === "function"
                ) {
                  onPreCreatedDocumentProcessed(doc.requestId);
                }
              }
            }
          }
        });

        unsubscribes.push(unsubscribe);
      }
    });

    // Update previous documentIds ref
    previousDocumentIds.current = [...documentIds];

    return () => {
      unsubscribes.forEach((unsubscribe) => {
        if (unsubscribe) {
          unsubscribe();
        }
      });
    };
  }, [documentIds, id]);

  const handleReplaceFile = (fileIndex = null) => {
    // For multi-file, store the index to replace
    if (
      (id === "damage_receipt" ||
        id === "third_party_receipt" ||
        id === "recourse_receipt" ||
        id === "depreciation_receipt" ||
        id === "additional_document") &&
      fileIndex !== null
    ) {
      replaceFileIndex.current = fileIndex;
    }
    // Just trigger file input click without clearing states
    document.getElementById(`file-input-${id}`).click();
  };

  const handleDeleteFile = (fileIndex) => {
    // Remove file from all arrays
    const newFiles = files.filter((_, index) => index !== fileIndex);
    const newFileInfos = fileInfos.filter((_, index) => index !== fileIndex);
    const newFilePreviewUrls = filePreviewUrls.filter(
      (_, index) => index !== fileIndex
    );
    const newUploadedFileUrls = uploadedFileUrls.filter(
      (_, index) => index !== fileIndex
    );
    const newDocumentIds = documentIds.filter(
      (_, index) => index !== fileIndex
    );
    const newFileSpinners = fileSpinners.filter(
      (_, index) => index !== fileIndex
    );

    // Update status and data arrays
    const newDocumentStatus = documentStatus
      ? documentStatus.filter((_, index) => index !== fileIndex)
      : null;
    const newDocumentData = documentData
      ? documentData.filter((_, index) => index !== fileIndex)
      : null;

    setFiles(newFiles);
    setFileInfos(newFileInfos);
    setFilePreviewUrls(newFilePreviewUrls);
    setUploadedFileUrls(newUploadedFileUrls);
    setDocumentIds(newDocumentIds);
    setFileSpinners(newFileSpinners);
    setDocumentStatus(newDocumentStatus);
    setDocumentData(newDocumentData);

    // Update previousDocumentIds ref to reflect the deletion
    previousDocumentIds.current = newDocumentIds;

    // If no files left, reset to initial state
    if (newFiles.length === 0) {
      setAllUploadsReady(false);
      hasMarkedReady.current = false;
      setIsUploadingFiles(false);
      reportedDocumentIds.current = {};
      previousDocumentIds.current = [];
      // Re-register with upload context to ensure proper state management
      registerFileUpload(id);

      // Notify parent with null data when all files are deleted
      if (onDataUpdate && typeof onDataUpdate === "function") {
        onDataUpdate(null);
      }
    } else {
      // Notify parent component with updated data only if we have valid data
      if (
        onDataUpdate &&
        typeof onDataUpdate === "function" &&
        newDocumentData &&
        newDocumentData.length > 0
      ) {
        // Filter out any undefined or null entries before passing to parent
        const validDocumentData = newDocumentData.filter(
          (data) => data != null
        );
        if (validDocumentData.length > 0) {
          onDataUpdate(validDocumentData);
        } else {
          onDataUpdate(null);
        }
      }
    }
  };

  const handleAddFile = () => {
    // For multi-file, just trigger file input to add more files
    document.getElementById(`file-input-${id}`).click();
  };

  // Handler to create a placeholder entry when no actual file is provided
  const handleCreatePlaceholderFile = async () => {
    const placeholderFile = {
      name: "Dosya Yok",
      type: "no-file",
      size: 0,
    };

    const isMultiFileType =
      id === "damage_receipt" ||
      id === "third_party_receipt" ||
      id === "recourse_receipt" ||
      id === "depreciation_receipt" ||
      id === "additional_document";

    // Decide whether to append or reset based on multi-file capability
    const newFiles =
      isMultiFileType && files.length > 0
        ? [...files, placeholderFile]
        : [placeholderFile];
    const newFileInfos =
      isMultiFileType && fileInfos.length > 0
        ? [
            ...fileInfos,
            {
              name: placeholderFile.name,
              type: placeholderFile.type,
              size: placeholderFile.size,
            },
          ]
        : [
            {
              name: placeholderFile.name,
              type: placeholderFile.type,
              size: placeholderFile.size,
            },
          ];
    const newPreviewUrls =
      isMultiFileType && filePreviewUrls.length > 0
        ? [...filePreviewUrls, null]
        : [null];
    const newUploadedUrls =
      isMultiFileType && uploadedFileUrls.length > 0
        ? [...uploadedFileUrls, "no-file"]
        : ["no-file"];
    const newFileSpinners =
      isMultiFileType && fileSpinners.length > 0
        ? [...fileSpinners, false]
        : [false];

    // Update local preview states
    setFiles(newFiles);
    setFileInfos(newFileInfos);
    setFilePreviewUrls(newPreviewUrls);
    setUploadedFileUrls(newUploadedUrls);
    setFileSpinners(newFileSpinners);
    setIsUploadingFiles(false);

    // Create a corresponding document in Firestore so that the usual flow continues
    if (user && requestId) {
      try {
        const timestamp = Date.now();
        const sourceId = `no-file_${timestamp}`;

        const docId = await createDocument({
          type: FILE_TYPES[id], // custom placeholder type
          url: "no-file",
          extension: "no-file",
          sourceId,
          requestId,
          status: "CREATED",
          name: placeholderFile.name,
          subtype: id,
          selectedTemplateId,
          // logging helpers
          user_email: user?.email || "",
          domain: window.location.hostname,
          petition_type: selectedTemplateId || "",
        });

        setDocumentIds((prevIds) => {
          if (isMultiFileType && prevIds && prevIds.length > 0) {
            return [...prevIds, docId];
          }
          return [docId];
        });
      } catch (err) {
        console.error("Error creating no-file document", err);
        setError("Belge oluşturulamadı");
      }
    }

    setAllUploadsReady(true);

    // Mark upload ready if not already
    if (!hasMarkedReady.current) {
      markFileUploadReady(id);
      hasMarkedReady.current = true;
    }
  };

  // Prevent placeholder click from triggering when arrow button clicked
  const handlePlaceholderClick = (e) => {
    // If the click originated from the arrow button, ignore
    if (e.target.closest && e.target.closest(".placeholder-right-button")) {
      return;
    }
    document.getElementById(`file-input-${id}`).click();
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // If we have a replace index set for multi-file, handle replacement
    if (
      replaceFileIndex.current !== null &&
      (id === "damage_receipt" ||
        id === "third_party_receipt" ||
        id === "recourse_receipt" ||
        id === "depreciation_receipt" ||
        id === "additional_document") &&
      selectedFiles.length > 0
    ) {
      const indexToReplace = replaceFileIndex.current;
      replaceFileIndex.current = null; // reset

      // Only take the first selected file for replacement
      replaceFileAtIndex(indexToReplace, selectedFiles[0]);
      return; // exit early, do not proceed with normal flow
    }

    if (selectedFiles.length > 0) {
      // For receipt types with existing files, add to existing files instead of replacing
      if (
        (id === "damage_receipt" ||
          id === "third_party_receipt" ||
          id === "recourse_receipt" ||
          id === "depreciation_receipt" ||
          id === "additional_document") &&
        files.length > 0
      ) {
        // Add new files to existing ones
        const combinedFiles = [...files, ...selectedFiles];
        setFiles(combinedFiles);

        const newFileInfos = selectedFiles.map((file) => ({
          name: file.name,
          type: file.type,
          size: file.size,
        }));
        setFileInfos([...fileInfos, ...newFileInfos]);

        const previewUrls = selectedFiles.map((file) => {
          if (file.type.startsWith("image/")) {
            return URL.createObjectURL(file);
          } else if (file.type === "application/pdf") {
            return URL.createObjectURL(file);
          }
          return null;
        });
        setFilePreviewUrls([...filePreviewUrls, ...previewUrls]);

        // Initialize spinners for new files
        setFileSpinners([
          ...fileSpinners,
          ...new Array(selectedFiles.length).fill(true),
        ]);

        // Auto-upload only new files
        handleUpload(selectedFiles, true);
      } else {
        // Clear existing files and states only when new files are selected (for single file or initial multi-file)
        setFiles([]);
        setUploadedFileUrls([]);
        setDocumentIds([]);
        setDocumentStatus(null);
        setDocumentData(null);
        setFilePreviewUrls([]);
        setFileInfos([]);
        setAllUploadsReady(false);
        hasMarkedReady.current = false;
        setIsUploadingFiles(true);
        // Reset reported document IDs for the new upload
        reportedDocumentIds.current = {};

        // For receipt types, allow multiple files
        if (
          id === "damage_receipt" ||
          id === "third_party_receipt" ||
          id === "recourse_receipt" ||
          id === "depreciation_receipt" ||
          id === "additional_document"
        ) {
          setFiles(selectedFiles);

          const newFileInfos = selectedFiles.map((file) => ({
            name: file.name,
            type: file.type,
            size: file.size,
          }));
          setFileInfos(newFileInfos);

          const previewUrls = selectedFiles.map((file) => {
            if (file.type.startsWith("image/")) {
              return URL.createObjectURL(file);
            } else if (file.type === "application/pdf") {
              return URL.createObjectURL(file);
            }
            return null;
          });
          setFilePreviewUrls(previewUrls);

          // Initialize spinners for each file to true
          setFileSpinners(new Array(selectedFiles.length).fill(true));

          // Auto-upload files
          handleUpload(selectedFiles);
        } else {
          // For other types, keep the single file behavior
          const selectedFile = selectedFiles[0];
          setFiles([selectedFile]);

          setFileInfos([
            {
              name: selectedFile.name,
              type: selectedFile.type,
              size: selectedFile.size,
            },
          ]);

          if (selectedFile.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = (e) => setFilePreviewUrls([e.target.result]);
            reader.readAsDataURL(selectedFile);
          } else if (selectedFile.type === "application/pdf") {
            setFilePreviewUrls([URL.createObjectURL(selectedFile)]);
          } else {
            setFilePreviewUrls([null]);
          }

          // Set spinner for this file
          setFileSpinners([true]);

          // Auto-upload file
          handleUpload([selectedFile]);
        }

        setError(null);

        // Mark as ready only once - ensure this happens for new uploads after deletion
        if (!hasMarkedReady.current) {
          setTimeout(() => {
            markFileUploadReady(id);
            hasMarkedReady.current = true;
          }, 100);
        }
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Use relatedTarget to check if we're actually leaving the drop zone
    // If relatedTarget is null or not within the drop zone, we're truly leaving
    const dropZone = e.currentTarget;
    const relatedTarget = e.relatedTarget;

    if (!relatedTarget || !dropZone.contains(relatedTarget)) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Show global uploading spinner only if there are no existing files
      if (files.length === 0) {
        setIsUploadingFiles(true);
      }

      const droppedFiles = Array.from(e.dataTransfer.files);

      if (
        id === "damage_receipt" ||
        id === "third_party_receipt" ||
        id === "recourse_receipt" ||
        id === "depreciation_receipt" ||
        id === "additional_document"
      ) {
        // For receipt types that allow multiple files, APPEND the dropped files rather than replacing
        const combinedFiles = [...files, ...droppedFiles];
        setFiles(combinedFiles);

        // Generate file info for the newly dropped files and merge with the existing
        const newFileInfos = droppedFiles.map((file) => ({
          name: file.name,
          type: file.type,
          size: file.size,
        }));
        setFileInfos([...fileInfos, ...newFileInfos]);

        // Generate preview URLs for the newly dropped files and merge
        const previewUrls = droppedFiles.map((file) => {
          if (file.type.startsWith("image/")) {
            return URL.createObjectURL(file);
          } else if (file.type === "application/pdf") {
            return URL.createObjectURL(file);
          }
          return null;
        });
        setFilePreviewUrls([...filePreviewUrls, ...previewUrls]);

        // Extend spinners array to cover the new files
        setFileSpinners([
          ...fileSpinners,
          ...new Array(droppedFiles.length).fill(true),
        ]);

        // Upload only the newly added files but merge metadata on completion
        handleUpload(droppedFiles, true);
      } else {
        // For other types, just use the first file
        setFiles([droppedFiles[0]]);

        // Set file info for single file
        setFileInfos([
          {
            name: droppedFiles[0].name,
            type: droppedFiles[0].type,
            size: droppedFiles[0].size,
          },
        ]);

        // Set preview URL for single file
        if (droppedFiles[0].type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => setFilePreviewUrls([e.target.result]);
          reader.readAsDataURL(droppedFiles[0]);
        } else if (droppedFiles[0].type === "application/pdf") {
          setFilePreviewUrls([URL.createObjectURL(droppedFiles[0])]);
        } else {
          setFilePreviewUrls([null]);
        }

        // Set spinner for this file
        setFileSpinners([true]);

        handleUpload([droppedFiles[0]]);
      }

      // Mark as ready only once
      if (!hasMarkedReady.current) {
        markFileUploadReady(id);
        hasMarkedReady.current = true;
      }
    }
  };

  const handleUpload = async (selectedFiles, isAddingToExisting = false) => {
    const filesToUpload = selectedFiles || files;

    if (!filesToUpload.length) {
      setError("Please select a file first");
      return;
    }

    if (!user) {
      setError("Authentication error");
      return;
    }

    if (!requestId) {
      setError("Request ID not available. Please wait or refresh the page.");
      return;
    }

    try {
      setUploading(true);
      setProcessing(true);

      const uploadPromises = filesToUpload.map(async (file, index) => {
        const timestamp = Date.now() + index; // Add index to ensure unique timestamp
        const sourceId = `${timestamp}`;
        const extension = file.name.split(".").pop().toLowerCase();
        const base = getClientId();
        const filePath = `${id}/${base}/${requestId}_${sourceId}.${extension}`;

        const url = await uploadFile(file, filePath, (progressValue) => {
          // Progress tracking removed - could be re-implemented if needed
        });

        // Create document in Firestore
        const docId = await createDocument({
          type: FILE_TYPES[id],
          url: url,
          extension: extension,
          sourceId,
          requestId: requestId,
          status: "CREATED",
          name: file.name || "",
          subtype: id,
          selectedTemplateId,
          // Add user information for logging
          user_email: user?.email || "",
          domain: window.location.hostname,
          petition_type: selectedTemplateId || "",
        });

        return { url, docId };
      });

      const results = await Promise.all(uploadPromises);

      // Update state with results
      if (isAddingToExisting) {
        // Add to existing arrays
        setUploadedFileUrls([
          ...uploadedFileUrls,
          ...results.map((r) => r.url),
        ]);
        setDocumentIds([...documentIds, ...results.map((r) => r.docId)]);
      } else {
        // Replace arrays
        setUploadedFileUrls(results.map((r) => r.url));
        setDocumentIds(results.map((r) => r.docId));
      }
    } catch (error) {
      console.error("Error in upload process:", error);
      setError("Failed to process file: " + error.message);

      // Reset spinners on error
      setFileSpinners(new Array(filesToUpload.length).fill(false));
      setIsUploadingFiles(false);
    } finally {
      setUploading(false);
      setProcessing(false);
      // Set isUploadingFiles to false when upload completes, not when processing completes
      setIsUploadingFiles(false);
    }
  };

  const renderMultipleFiles = () => {
    if (!files.length) {
      return <div className="no-file">No files selected</div>;
    }

    return files.map((file, index) => {
      const url = filePreviewUrls[index] || uploadedFileUrls[index];
      const info = fileInfos[index];
      const docId = documentIds[index];
      const status = documentStatus && documentStatus[index];
      const data = documentData && documentData[index];

      return (
        <div
          key={index}
          className="preview-results-container files-ready"
          style={{
            marginBottom: "10px",
            paddingBottom: "10px",
            position: "relative",
          }}
        >
          <div className="file-preview-container">
            {!info ? (
              <div className="file-info">
                <p>
                  <strong>File {index + 1}:</strong> Loading file information...
                </p>
              </div>
            ) : !url ? (
              <div className="file-info">
                <p>
                  <strong>File {index + 1}:</strong> {info.name}
                </p>
                <p>
                  <strong>Type:</strong> {info.type || "Unknown"}
                </p>
                <p>
                  <strong>Size:</strong> {(info.size / 1024).toFixed(2)} KB
                </p>
              </div>
            ) : info.type === "no-file" ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#ffffff",
                  border: "2px solid #31538fcf",
                  borderRadius: "8px",
                  color: "#6c757d",
                  textAlign: "center",
                  padding: "20px",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="8" y1="13" x2="16" y2="13" />
                  <line x1="8" y1="17" x2="16" y2="17" />
                </svg>
                <span style={{ marginTop: "10px", fontStyle: "italic" }}>
                  Dosya Yüklenmedi
                </span>
              </div>
            ) : info.type.startsWith("image/") ? (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <img
                  src={url}
                  alt={`Preview ${index + 1}`}
                  className="file-preview-img"
                />
              </div>
            ) : info.type === "application/pdf" ? (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <object
                  data={url}
                  type="application/pdf"
                  width="100%"
                  className="pdf-viewer"
                >
                  <param name="zoom" value="150%" />
                  <param name="view" value="FitH" />
                  <param name="initialView" value="fitH" />
                  <embed
                    src={url}
                    type="application/pdf"
                    width="100%"
                    zoom="150%"
                  />
                </object>
              </div>
            ) : (
              <div className="file-info">
                <p>
                  <strong>File {index + 1}:</strong> {info.name}
                </p>
                <p>
                  <strong>Type:</strong> {info.type || "Unknown"}
                </p>
                <p>
                  <strong>Size:</strong> {(info.size / 1024).toFixed(2)} KB
                </p>
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="download-link"
                  >
                    Download File
                  </a>
                )}
              </div>
            )}

            {/* Individual file buttons */}
            <div
              className="floating-button-container"
              style={{ bottom: "10px", right: "10px" }}
            >
              {/* Remove button */}
              <button
                className="floating-remove-button"
                onClick={() => handleDeleteFile(index)}
                title="Dosyayı Sil"
                style={{
                  width: "35px",
                  height: "35px",
                  right: "50px",
                  position: "absolute",
                  backgroundColor: "#d32f2f",
                  boxShadow: "0 4px 12px rgba(211, 47, 47, 0.3)",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              {/* Edit file button */}
              <button
                className="floating-replace-button"
                onClick={() => handleReplaceFile(index)}
                title="Dosyayı Düzenle"
                style={{
                  width: "35px",
                  height: "35px",
                  backgroundColor: "#5772a3",
                  boxShadow: "0 4px 12px rgba(87, 114, 163, 0.3)",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                  <path d="m15 5 4 4" />
                </svg>
              </button>
            </div>
          </div>

          <div className={`results-container ${docId ? "has-results" : ""}`}>
            {uploading ||
            processing ||
            (docId && (status === "CREATED" || status === "PENDING")) ? (
              <div
                className="results-processing"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  minHeight: "200px",
                  background: "transparent",
                }}
              >
                <Lottie
                  animationData={waitingAnimation}
                  style={{ width: 150, height: 150 }}
                />
              </div>
            ) : docId &&
              status === "DONE" &&
              data &&
              data.type === "receipt" ? (
              <div className="success-message">
                {renderReceiptFields(data, index)}
              </div>
            ) : null}
          </div>
        </div>
      );
    });
  };

  const renderFilePreview = () => {
    // If this is the special pre_created_document flow, never show the file preview – keep the spinner instead.
    if (id === "pre_created_document") {
      return null;
    }
    // Container style for file previews
    const containerStyle = {
      height: "65vh",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      overflowY: "auto",
    };

    const multipleFileContainerStyle = {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      overflowY: "auto",
    };
    // If we have receipt types and multiple files, use the new multi-container layout
    if (
      (id === "damage_receipt" ||
        id === "third_party_receipt" ||
        id === "recourse_receipt" ||
        id === "depreciation_receipt" ||
        id === "additional_document") &&
      files.length > 0
    ) {
      return (
        <div
          style={multipleFileContainerStyle}
          className="multiple-files-container"
        >
          {renderMultipleFiles()}
          {renderUploadPlaceholder()}
        </div>
      );
    } else {
      // For single file uploads (original behavior for non-receipt types)
      // If no files at all, return null so placeholder shows
      if (files.length === 0) {
        return null;
      }

      const url = filePreviewUrls[0] || uploadedFileUrls[0];
      const info = fileInfos[0];

      if (!info) {
        return <div className="no-file">Loading file information...</div>;
      }

      if (!url && info) {
        return (
          <div className="file-info" style={containerStyle}>
            <p>
              <strong>File:</strong> {info.name}
            </p>
            <p>
              <strong>Type:</strong> {info.type || "Unknown"}
            </p>
            <p>
              <strong>Size:</strong> {(info.size / 1024).toFixed(2)} KB
            </p>
          </div>
        );
      }

      if (info && info.type.startsWith("image/")) {
        return (
          <img
            src={url}
            alt="Preview"
            className="file-preview-img"
            style={containerStyle}
          />
        );
      } else if (info && info.type === "application/pdf") {
        return (
          <div className="file-preview-pdf" style={containerStyle}>
            <object
              data={url}
              type="application/pdf"
              width="100%"
              height="100%"
              className="pdf-viewer"
            >
              <param name="zoom" value="150%" />
              <param name="view" value="FitH" />
              <param name="initialView" value="fitH" />
              <embed
                src={url}
                type="application/pdf"
                width="100%"
                height="100%"
                zoom="150%"
              />
            </object>
          </div>
        );
      } else if (
        info &&
        (info.type === "application/msword" ||
          info.type ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
      ) {
        return (
          <div className="file-info" style={containerStyle}>
            <p>
              <strong>File:</strong> {info.name}
            </p>
            <p>
              <strong>Type:</strong> {info.type || "Unknown"}
            </p>
            <p>
              <strong>Size:</strong> {(info.size / 1024).toFixed(2)} KB
            </p>
            {url && (
              <div className="word-document-preview">
                <iframe
                  title="word-document-iframe"
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    url
                  )}`}
                  width="100%"
                  frameBorder="0"
                  className="word-document-iframe"
                ></iframe>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-link"
                >
                  Download Document
                </a>
              </div>
            )}
          </div>
        );
      } else if (info && info.type === "no-file") {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#ffffff",
              border: "2px solid #31538fcf",
              borderRadius: "8px",
              color: "#6c757d",
              textAlign: "center",
              padding: "20px",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="16" y2="17" />
            </svg>
            <span style={{ marginTop: "10px", fontStyle: "italic" }}>
              Dosya Yüklenmedi
            </span>
          </div>
        );
      } else if (info) {
        return (
          <div className="file-info" style={containerStyle}>
            <p>
              <strong>File:</strong> {info.name}
            </p>
            <p>
              <strong>Type:</strong> {info.type || "Unknown"}
            </p>
            <p>
              <strong>Size:</strong> {(info.size / 1024).toFixed(2)} KB
            </p>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="download-link"
              >
                Download File
              </a>
            )}
          </div>
        );
      }
    }

    return <div className="no-file">No file information available</div>;
  };

  const renderResults = () => {
    if (
      uploading ||
      processing ||
      (documentIds.length > 0 &&
        documentStatus &&
        documentStatus.every(
          (status) => status === "CREATED" || status === "PENDING"
        ))
    ) {
      return (
        <div
          className="results-processing"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "200px",
          }}
        >
          <Lottie
            animationData={waitingAnimation}
            style={{ width: 150, height: 150 }}
          />
        </div>
      );
    }

    if (
      documentData &&
      documentData.every((data) => data.type === "petition")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("arbitrationNumber")}
                value={data.arbitrationNumber}
                fieldId="arbitrationNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantName")}
                value={data.applicantName}
                fieldId="applicantName"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantAttorney")}
                value={data.applicantAttorney}
                fieldId="applicantAttorney"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantLicensePlate")}
                value={data.applicantLicensePlate}
                fieldId="applicantLicensePlate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("otherPartyLicensePlate")}
                value={data.otherPartyLicensePlate}
                fieldId="otherPartyLicensePlate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "petition1")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("arbitrationNumber")}
                value={data.arbitrationNumber}
                fieldId="arbitrationNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantName")}
                value={data.applicantName}
                fieldId="applicantName"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantAttorney")}
                value={data.applicantAttorney}
                fieldId="applicantAttorney"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantLicensePlate")}
                value={data.applicantLicensePlate}
                fieldId="applicantLicensePlate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("otherPartyLicensePlate")}
                value={data.otherPartyLicensePlate}
                fieldId="otherPartyLicensePlate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("accidentDate")}
                value={data.accidentDate}
                fieldId="accidentDate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "petition2")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("arbitrationNumber")}
                value={data.arbitrationNumber}
                fieldId="arbitrationNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantName")}
                value={data.applicantName}
                fieldId="applicantName"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantAttorney")}
                value={data.applicantAttorney}
                fieldId="applicantAttorney"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("otherPartyLicensePlate")}
                value={data.otherPartyLicensePlate}
                fieldId="otherPartyLicensePlate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("accidentDate")}
                value={data.accidentDate}
                fieldId="accidentDate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "petition3")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("arbitrationNumber")}
                value={data.arbitrationNumber}
                fieldId="arbitrationNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantName")}
                value={data.applicantName}
                fieldId="applicantName"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantAttorney")}
                value={data.applicantAttorney}
                fieldId="applicantAttorney"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("otherPartyLicensePlate")}
                value={data.otherPartyLicensePlate}
                fieldId="otherPartyLicensePlate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "petition4")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("arbitrationNumber")}
                value={data.arbitrationNumber}
                fieldId="arbitrationNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantName")}
                value={data.applicantName}
                fieldId="applicantName"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantAttorney")}
                value={data.applicantAttorney}
                fieldId="applicantAttorney"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("accidentDate")}
                value={data.accidentDate}
                fieldId="accidentDate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicationDate")}
                value={data.applicationDate}
                fieldId="applicationDate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "receipt")
    ) {
      return renderReceiptResults();
    } else if (
      documentData &&
      documentData.every((data) => data.type === "policy")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("policyNumber")}
                value={data.policyNumber}
                fieldId="policyNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantLicensePlate")}
                value={data.applicantLicensePlate}
                fieldId="applicantLicensePlate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "policy1")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("policyNumber")}
                value={data.policyNumber}
                fieldId="policyNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantLicensePlate")}
                value={data.applicantLicensePlate}
                fieldId="applicantLicensePlate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("policyStartDate")}
                value={data.policyStartDate}
                fieldId="policyStartDate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("policyIssuanceTime")}
                value={data.policyIssuanceTime}
                fieldId="policyIssuanceTime"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "expert_report")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("totalDepreciation")}
                value={data.totalDepreciation}
                fieldId="totalDepreciation"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("accidentDate")}
                value={data.accidentDate}
                fieldId="accidentDate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "expert_report1")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("accidentDate")}
                value={data.accidentDate}
                fieldId="accidentDate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "expert_report2")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("totalDepreciation")}
                value={data.totalDepreciation}
                fieldId="totalDepreciation"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "expert_report3")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("totalDamage")}
                value={data.totalDamage}
                fieldId="totalDamage"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "authority_report")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("arbitrationNumber")}
                value={data.arbitrationNumber}
                fieldId="arbitrationNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantName")}
                value={data.applicantName}
                fieldId="applicantName"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("applicantAttorney")}
                value={data.applicantAttorney}
                fieldId="applicantAttorney"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "arbitration_decision")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("concludedArbitrationCaseNumber")}
                value={data.concludedArbitrationCaseNumber}
                fieldId="concludedArbitrationCaseNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("concludedArbitrationDecisionDate")}
                value={data.concludedArbitrationDecisionDate}
                fieldId="concludedArbitrationDecisionDate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("concludedArbitrationDecisionNumber")}
                value={data.concludedArbitrationDecisionNumber}
                fieldId="concludedArbitrationDecisionNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("paymentAmountInConcludedCase")}
                value={data.paymentAmountInConcludedCase}
                fieldId="paymentAmountInConcludedCase"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    } else if (
      documentData &&
      documentData.every((data) => data.type === "arbitration_decision1")
    ) {
      return (
        <div className="success-message">
          {documentData.map((data, index) => (
            <div key={index} className="result-fields">
              <ResultField
                title={getFieldTitle("concludedArbitrationDecisionDate")}
                value={data.concludedArbitrationDecisionDate}
                fieldId="concludedArbitrationDecisionDate"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("concludedArbitrationDecisionNumber")}
                value={data.concludedArbitrationDecisionNumber}
                fieldId="concludedArbitrationDecisionNumber"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              <ResultField
                title={getFieldTitle("paymentAmountInConcludedCase")}
                value={data.paymentAmountInConcludedCase}
                fieldId="paymentAmountInConcludedCase"
                onValueChange={(fieldId, newValue) =>
                  handleFieldUpdate(index, fieldId, newValue)
                }
              />
              {showErrorFields && (
                <ResultField title="Error" value={data.error} fieldId="error" />
              )}
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const hasResults = () => {
    return (
      uploading ||
      processing ||
      (documentIds.length > 0 && documentStatus) ||
      (documentIds.length > 0 && documentData)
    );
  };

  // Update the field update handler to use fieldId directly
  const handleFieldUpdate = (docIndex, fieldId, newValue) => {
    setDocumentData((prevData) => {
      // Create deep copy of the data
      const newData = [...prevData];
      // Update the specific field using the fieldId directly
      newData[docIndex] = {
        ...newData[docIndex],
        [fieldId]: newValue,
      };
      // Call onDataUpdate with the updated data
      if (onDataUpdate && typeof onDataUpdate === "function") {
        onDataUpdate(newData);
      }

      return newData;
    });
  };

  if (requestError && !requestId) {
    return (
      <div className="file-upload-box" id={`upload-${id}`}>
        <h3>{title}</h3>
        <div className="error">Failed to create request: {requestError}</div>
      </div>
    );
  }

  // Render the spinner instead of the file-preview-placeholder when uploading
  const renderUploadPlaceholder = () => {
    // Keep showing the loading spinner for pre_created_document once a file is selected,
    // irrespective of the internal uploading flags, until the component is unmounted / replaced.
    if (
      (id === "pre_created_document" && files.length > 0) ||
      isUploadingFiles ||
      (files.length > 0 && !allFileUploadsComplete)
    ) {
      return (
        <div className="file-preview-placeholder upload-spinner-container">
          <div className="success-spinner">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="success-spinner-dot" />
            ))}
          </div>
        </div>
      );
    } else {
      return (
        <div
          className={`placeholder-wrapper`}
          style={{
            display: "flex",
            position: "relative",
            width: "100%",
            margin: allFileUploadsComplete ? "0" : "0 7vh",
            height: allFileUploadsComplete ? "15vh" : undefined,
          }}
        >
          <div
            className={`file-preview-placeholder${
              isDragActive ? " active-drop" : ""
            }${isOptional ? " optional-placeholder" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handlePlaceholderClick}
            style={{
              cursor: "pointer",
              flex: 1,
              position: "relative",
              zIndex: 1,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                zIndex: 1,
              }}
            >
              <img
                src={require("../assets/images/download_icon.png")}
                alt="Download Icon"
                style={{
                  width: "38px",
                  marginBottom: "10px",
                  display: "block",
                  opacity: isOptional ? 0.7 : 1,
                }}
              />
              <div
                className="drop-zone-text"
                style={{ opacity: isOptional ? 0.8 : 1 }}
              >
                {isOptional && "Opsiyonel: "}
                {id === "damage_receipt" ||
                id === "third_party_receipt" ||
                id === "recourse_receipt" ||
                id === "depreciation_receipt" ||
                id === "additional_document"
                  ? "Dosyaları buraya sürükleyin veya yüklemek için tıklayın"
                  : "Dosyayı buraya sürükleyin veya yüklemek için tıklayın"}
              </div>
            </div>
            {isDragActive && <div className="drag-animation-overlay"></div>}
          </div>
          {id !== "pre_created_document" && (
            <button
              className="placeholder-right-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCreatePlaceholderFile();
              }}
              style={{
                width: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#5772a3",
                border: "none",
                color: "#fff",
                fontSize: "24px",
                cursor: "pointer",
                padding: 0,
                borderTopRightRadius: "10px",
                borderBottomRightRadius: "10px",
                marginLeft: "-7px",
                zIndex: 2,
              }}
              title="Dosya Seç"
            >
              &gt;
            </button>
          )}
        </div>
      );
    }
  };

  return (
    <div className={`file-upload-box ${className || ""}`} id={`upload-${id}`}>
      <div className="title-upload-container">
        <h3>
          {title}
          {isOptional && " (Opsiyonel)"}
        </h3>
      </div>

      {error && <div className="error">{error}</div>}

      <input
        type="file"
        id={`file-input-${id}`}
        onChange={handleFileChange}
        style={{ display: "none" }}
        multiple={
          id === "damage_receipt" ||
          id === "third_party_receipt" ||
          id === "recourse_receipt" ||
          id === "depreciation_receipt" ||
          id === "additional_document"
        }
      />

      <div
        className={`preview-results-container ${
          allUploadsReady ? "files-ready" : ""
        } ${className || ""}`}
      >
        {/* Show loading while uploading files OR while waiting for all uploads to complete */}
        {(id === "pre_created_document" && files.length > 0) ||
        isUploadingFiles ||
        (files.length > 0 && !allFileUploadsComplete) ? (
          renderUploadPlaceholder()
        ) : files.length === 0 ? (
          renderUploadPlaceholder()
        ) : (
          <>
            <div className={`file-preview-container ${className || ""}`}>
              {renderFilePreview()}
              {/* For single file uploads, show replace button and remove button for optional files */}
              {allUploadsReady &&
                id !== "damage_receipt" &&
                id !== "third_party_receipt" &&
                id !== "recourse_receipt" &&
                id !== "depreciation_receipt" &&
                id !== "additional_document" && (
                  <div className="floating-button-container">
                    {/* Remove button for optional single files */}
                    {isOptional && (
                      <button
                        className="floating-remove-button"
                        onClick={() => handleDeleteFile(0)}
                        title="Dosyayı Sil"
                        style={{
                          width: "35px",
                          height: "35px",
                          right: "50px",
                          position: "absolute",
                          backgroundColor: "#d32f2f",
                          boxShadow: "0 4px 12px rgba(211, 47, 47, 0.3)",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    )}
                    {/* Edit file button */}
                    <button
                      className="floating-replace-button"
                      onClick={() => handleReplaceFile(0)}
                      title="Dosyayı Düzenle"
                      style={{
                        width: "35px",
                        height: "35px",
                        backgroundColor: "#5772a3",
                        boxShadow: "0 4px 12px rgba(87, 114, 163, 0.3)",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        <path d="m15 5 4 4" />
                      </svg>
                    </button>
                  </div>
                )}
            </div>
            {id !== "damage_receipt" &&
              id !== "third_party_receipt" &&
              id !== "recourse_receipt" &&
              id !== "depreciation_receipt" &&
              id !== "additional_document" && (
                <div
                  className={`results-container ${
                    hasResults() ? "has-results" : ""
                  }`}
                >
                  {renderResults()}
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
